import React from "react"
import { graphql, Link, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

export const walls = {
  fr: {
    title: "À chaque chantier son Précoffré®",
    subtitle:
      "Besoin d’isolation ? Optez pour le Thermique. Combinez technique et finition exceptionnelle avec le Design. Gagnez 44% de productivité avec ses équipements.",
    products: [
      {
        name: "Classique",
        url: "/premur-mur-prefabrique/",
        img: "classique",
      },
      {
        name: "Thermique",
        url: "/premur-mur-prefabrique/thermique/",
        img: "thermique",
      },
      {
        name: "Design",
        url: "/premur-mur-prefabrique/architectural/",
        img: "architectural",
      },
      {
        name: "TH Green",
        url: "/premur-mur-prefabrique/bas-carbone/",
        img: "bascarbone",
      },
      {
        name: "Précoffré® et équipements",
        url: "/premur-mur-prefabrique/integral/",
        img: "integral",
      },
    ],
  },

  de: {
    title: "Für jede Baustelle die geeignete Doppelwand",
    subtitle:
      "Brauchen Sie Wärmedämmung? Wählen Sie die Thermowand. Oder kombinieren Sie die fortgeschrittenste Technik mit einem außergewöhnlichen Finish und entscheiden Sie sich für die Architektonische Doppelwand. Gewinnen Sie 44% an Produktivität mit der Integralen Doppelwand, der einzigen Fertigwand, die den Einbau bei optimierter Sicherheit und verstärkter Leistung erleichtert.",
    products: [
      {
        name: "Klassich",
        url: "/de/doppelwand/",
        img: "classique",
      },
      {
        name: "Thermowand",
        url: "/de/doppelwand/thermo/",
        img: "thermique",
      },
      {
        name: "Design",
        url: "/de/doppelwand/architektonische/",
        img: "bascarbone",
      },
      {
        name: "Green",
        url: "/de/doppelwand/green/",
        img: "architectural",
      },
      {
        name: "Zubehör",
        url: "/de/doppelwand/integral/",
        img: "integral",
      },
    ],
  },

  en: {
    title: "A precast wall for every construction project",
    subtitle:
      "Need insulation? Opt for Thermal. Combine exceptional technology and finishing with Architectural. Gain 44% productivity with Integral, the only precast wall that promises ease of installation, while optimising safety and performance on site.",
    products: [
      {
        name: "Classic",
        url: "/en/precast-wall/",
        img: "classique",
      },
      {
        name: "Thermal",
        url: "/en/precast-wall/thermal/",
        img: "thermique",
      },
      {
        name: "TH Green",
        url: "/en/precast-wall/green",
        img: "bascarbone",
      },
      {
        name: "Design",
        url: "/en/precast-wall/architectural/",
        img: "architectural",
      },
      {
        name: "Equipment",
        url: "/en/precast-wall/integral/",
        img: "integral",
      },
    ],
  },
}

const AllPrecos = ({ lang }) => {
  const data = useStaticQuery(graphql`
    {
      architectural: file(
        name: { eq: "premur_architectural_3D" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      integral: file(
        sourceInstanceName: { eq: "images" }
        name: { eq: "premur_integral_3D" }
      ) {
        ...Constrained
      }
      bascarbone: file(
        name: { eq: "mur_precoffre_TH_Green" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      classique: file(
        name: { eq: "premur_classique_3D" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      thermique: file(
        name: { eq: "premur_thermique_3D" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
    }
  `)

  return (
    <section>
      <div className="mb-12 ml-10 flex max-w-3xl flex-col items-start border-l-4 border-fehr-500 pl-3">
        <h2 className="mb-4">{walls[lang].title}</h2>
        <p className="mb-4">{walls[lang].subtitle}</p>
      </div>
      <div className="grid-row-1 grid grid-cols-2 gap-2 px-2 *:relative *:bg-gray-100 *:py-3 lg:max-h-lvh lg:grid-cols-3 lg:grid-rows-2 [&_>a_p]:absolute [&_>a_p]:inset-x-4 [&_>a_p]:bottom-4 [&_>a_p]:rounded-sm [&_>a_p]:bg-fehr-500/90 [&_>a_p]:p-10 [&_>a_p]:py-2 [&_>a_p]:text-center [&_>a_p]:text-white">
        <Link
          className="transition-all hover:bg-gray-200 max-lg:h-[40vh] lg:col-span-1 lg:row-span-1"
          to={walls[lang].products[0].url}
        >
          <GatsbyImage
            image={getImage(data.classique)!}
            alt={data.classique.name || ""}
            objectFit="contain"
            className="h-full"
          />
          <p>{walls[lang].products[0].name}</p>
        </Link>
        <Link
          className="transition-all hover:bg-gray-200 max-lg:h-[40vh] lg:col-start-2 lg:col-end-3 lg:row-start-1 lg:row-end-2"
          to={walls[lang].products[1].url}
        >
          <GatsbyImage
            image={getImage(data.thermique)!}
            alt={data.thermique.name || ""}
            objectFit="contain"
            className="h-full"
          />
          <p>{walls[lang].products[1].name}</p>
        </Link>
        <Link
          className="transition-all hover:bg-gray-200 max-lg:h-[40vh] lg:col-span-1 lg:col-start-3 lg:row-start-1 lg:row-end-2"
          to={walls[lang].products[3].url}
        >
          <GatsbyImage
            image={getImage(data.bascarbone)!}
            alt={data.bascarbone.name || ""}
            objectFit="contain"
            className="h-full"
          />
          <p>{walls[lang].products[3].name}</p>
        </Link>
        <Link
          className="transition-all hover:bg-gray-200 max-lg:h-[40vh] lg:col-start-1 lg:col-end-2 lg:row-start-2 lg:row-end-3"
          to={walls[lang].products[2].url}
        >
          <GatsbyImage
            image={getImage(data.architectural)!}
            alt={data.architectural.name || ""}
            objectFit="contain"
            className="h-full"
          />
          <p>{walls[lang].products[2].name}</p>
        </Link>
        <Link
          className="transition-all hover:bg-gray-200 max-lg:h-[40vh] lg:col-start-2 lg:col-end-4 lg:row-start-2 lg:row-end-3"
          to={walls[lang].products[4].url}
        >
          <GatsbyImage
            image={getImage(data.integral)!}
            alt={data.integral.name || ""}
            objectFit="contain"
            className="h-full"
          />
          <p>{walls[lang].products[4].name}</p>
        </Link>
      </div>
    </section>
    //   className="my-10 flex flex-col items-center gap-y-10 text-center"
    //   id={props.id}
    // >
    //   <div className="w-[650px]	max-w-[90vw]">
    //     <h2 className="text-4xl lg:text-6xl">{walls[props.lang].title}</h2>
    //     <hr />
    //     <p className="mt-3 ">{walls[props.lang].subtitle}</p>
    //   </div>
    //   <div className="flex max-w-5xl flex-col items-stretch justify-center gap-y-4 md:flex-row">
    //     {walls[props.lang].products.map((product) => {
    //       const img = getImage(data[product.img])
    //       return (
    //         <Link
    //           className="flex flex-col text-black transition-all hover:bg-fehr-500"
    //           key={product.name}
    //           to={product.url}
    //         >
    //           <GatsbyImage
    //             objectFit="contain"
    //             image={img}
    //             alt={img}
    //             className="basis-5/6 "
    //           />
    //           <h3 className="basis-1/6">{product.name}</h3>
    //         </Link>
    //       )
    //     })}
    //   </div>
    // </section>
  )
}
export default AllPrecos
